import { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import SuperAdminLayout from '@/components/feature/SuperAdminLayout';
import SAHeader from '@/components/feature/SAHeader';
import LoadingState from '@/components/base/LoadingState';
import { supabase } from '@/lib/supabaseClient';
import { requireSuperAdmin } from '@/middleware/superAdminGuard';
import { formatStorageMb, formatStorageBytes } from '@/lib/getStorageUsage';
import UsageTab, { type TenantStorageRow } from './components/UsageTab';
import FilesTab from './components/FilesTab';
import VpsSettingsTab from './components/VpsSettingsTab';

// ── Types ──────────────────────────────────────────────────────────────────────
type TabId = 'overview' | 'usage' | 'files' | 'vps';

const TABS: { id: TabId; label: string; icon: string }[] = [
  { id: 'overview', label: 'Overview',     icon: 'ri-dashboard-line' },
  { id: 'usage',    label: 'Per Tenant',   icon: 'ri-pie-chart-line' },
  { id: 'files',    label: 'Files',        icon: 'ri-folder-open-line' },
  { id: 'vps',      label: 'Architecture', icon: 'ri-server-line' },
];

interface TenantRaw {
  id: string;
  name: string;
  slug: string;
  plan_code: string;
  status: string;
  storage_quota_mb: number;
  storage_used_mb: number;
}

// ── Helpers ────────────────────────────────────────────────────────────────────
function pct(used: number, quota: number) {
  if (!quota) return 0;
  return Math.min(100, Math.round((used / quota) * 100));
}

function barCls(p: number) {
  if (p >= 90) return 'bg-red-400';
  if (p >= 70) return 'bg-amber-400';
  if (p >= 50) return 'bg-[#C8A96E]';
  return 'bg-emerald-400';
}

// ── Page ──────────────────────────────────────────────────────────────────────
export default function SAStoragePage() {
  const navigate      = useNavigate();
  const [loading, setLoading]     = useState(true);
  const [rows, setRows]           = useState<TenantStorageRow[]>([]);
  const [activeTab, setActiveTab] = useState<TabId>('overview');
  const [fileCount, setFileCount] = useState(0);
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);

  // ── Fetch ──────────────────────────────────────────────────────────────────
  const fetchData = useCallback(async () => {
    setLoading(true);

    const [tenantsRes, filesCountRes] = await Promise.all([
      supabase
        .from('saas_tenants')
        .select('id, name, slug, plan_code, status, storage_quota_mb, storage_used_mb')
        .order('storage_used_mb', { ascending: false }),
      supabase
        .from('uploaded_files')
        .select('id', { count: 'exact', head: true }),
    ]);

    const tenants = (tenantsRes.data ?? []) as TenantRaw[];

    const combined: TenantStorageRow[] = tenants.map((t) => ({
      id:               t.id,
      name:             t.name,
      slug:             t.slug,
      plan_code:        t.plan_code,
      status:           t.status,
      storage_quota_mb: t.storage_quota_mb ?? 1024,
      // Use storage_used_mb from saas_tenants (authoritative source from backend)
      usedBytes:        (t.storage_used_mb ?? 0) * 1024 * 1024,
    }));

    setRows(combined);
    setFileCount(filesCountRes.count ?? 0);
    setLastUpdated(new Date());
    setLoading(false);
  }, []);

  useEffect(() => {
    const init = async () => {
      const ok = await requireSuperAdmin();
      if (!ok) { navigate('/login'); return; }
      await fetchData();
    };
    init();
  }, [navigate, fetchData]);

  const handleQuotaChange = (tenantId: string, newQuotaMb: number) => {
    setRows((prev) => prev.map((r) => r.id === tenantId ? { ...r, storage_quota_mb: newQuotaMb } : r));
  };

  // ── Derived stats ──────────────────────────────────────────────────────────
  const totalUsedBytes  = rows.reduce((s, r) => s + r.usedBytes, 0);
  const totalQuotaMb    = rows.reduce((s, r) => s + r.storage_quota_mb, 0);
  const totalUsedMb     = totalUsedBytes / (1024 * 1024);
  const platformPct     = pct(totalUsedMb, totalQuotaMb);
  const criticalTenants = rows.filter((r) => pct(r.usedBytes / (1024 * 1024), r.storage_quota_mb) >= 90);
  const warningTenants  = rows.filter((r) => {
    const p = pct(r.usedBytes / (1024 * 1024), r.storage_quota_mb);
    return p >= 70 && p < 90;
  });
  const topTenants      = [...rows]
    .sort((a, b) => b.usedBytes - a.usedBytes)
    .slice(0, 5);

  return (
    <SuperAdminLayout>
      <SAHeader
        title="Storage Center"
        subtitle="VPS storage management — metadata tracked in Supabase"
        actions={
          <div className="flex items-center gap-3">
            {!loading && (
              <div className={`flex items-center gap-2 px-3 py-1.5 rounded-full border text-xs font-semibold ${
                platformPct >= 90 ? 'bg-red-50 border-red-200 text-red-700' :
                platformPct >= 70 ? 'bg-amber-50 border-amber-200 text-amber-700' :
                'bg-emerald-50 border-emerald-200 text-emerald-700'
              }`}>
                <span className={`w-2 h-2 rounded-full ${
                  platformPct >= 90 ? 'bg-red-400' : platformPct >= 70 ? 'bg-amber-400 animate-pulse' : 'bg-emerald-400 animate-pulse'
                }`}></span>
                {platformPct}% · {rows.length} tenants
              </div>
            )}
            <button
              onClick={fetchData}
              disabled={loading}
              className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-slate-500 hover:text-slate-900 border border-slate-200 hover:bg-slate-50 rounded-lg transition-colors cursor-pointer disabled:opacity-40 whitespace-nowrap"
            >
              <i className={`ri-refresh-line text-sm ${loading ? 'animate-spin' : ''}`}></i>
              Refresh
            </button>
          </div>
        }
      />

      <div className="px-4 md:px-8 pt-5 pb-8">

        {/* ── VPS Provider Banner ── */}
        <div className="mb-5 flex items-center gap-4 px-5 py-4 bg-[#0f172a] rounded-2xl">
          <div className="w-10 h-10 flex items-center justify-center bg-white/10 rounded-xl shrink-0">
            <i className="ri-server-fill text-white text-lg"></i>
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-0.5">
              <p className="text-white text-sm font-bold">Storage Provider: VPS (Self-Hosted)</p>
              <span className="flex items-center gap-1 px-2 py-0.5 bg-white/10 rounded-full text-[11px] text-white/70 font-medium whitespace-nowrap">
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse"></span>
                Active
              </span>
            </div>
            <p className="text-white/50 text-xs leading-relaxed">
              File binaries stored on VPS infrastructure · Supabase role = metadata only (URLs, sizes, types) · No binaries in Supabase
            </p>
          </div>
          <div className="flex items-center gap-3 shrink-0">
            <div className="text-right">
              <p className="text-white/40 text-[10px] uppercase tracking-wider">Supabase</p>
              <p className="text-emerald-400 text-xs font-semibold">Metadata ✓</p>
            </div>
            <div className="w-px h-8 bg-white/10"></div>
            <div className="text-right">
              <p className="text-white/40 text-[10px] uppercase tracking-wider">VPS Binary</p>
              <p className="text-emerald-400 text-xs font-semibold">Active ✓</p>
            </div>
          </div>
        </div>

        {/* ── Tab bar ── */}
        <div className="flex items-center gap-1 bg-white border border-slate-200 rounded-xl p-1.5 mb-6 overflow-x-auto">
          {TABS.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium whitespace-nowrap cursor-pointer transition-all ${
                activeTab === tab.id
                  ? 'bg-[#0f172a] text-white'
                  : 'text-slate-500 hover:text-slate-700 hover:bg-slate-50'
              }`}
            >
              <span className="w-4 h-4 flex items-center justify-center"><i className={`${tab.icon} text-sm`}></i></span>
              {tab.label}
            </button>
          ))}
        </div>

        {/* ── Tab content ── */}
        {loading ? (
          <LoadingState />
        ) : (
          <>
            {activeTab === 'overview' && (
              <OverviewTab
                rows={rows}
                totalUsedMb={totalUsedMb}
                totalQuotaMb={totalQuotaMb}
                platformPct={platformPct}
                criticalTenants={criticalTenants}
                warningTenants={warningTenants}
                topTenants={topTenants}
                fileCount={fileCount}
                lastUpdated={lastUpdated}
                onGoToUsage={() => setActiveTab('usage')}
                onGoToFiles={() => setActiveTab('files')}
              />
            )}
            {activeTab === 'usage' && (
              <UsageTab rows={rows} onQuotaChange={handleQuotaChange} />
            )}
            {activeTab === 'files' && (
              <FilesTab tenants={rows.map((r) => ({ id: r.id, name: r.name }))} />
            )}
            {activeTab === 'vps' && <VpsSettingsTab />}
          </>
        )}
      </div>
    </SuperAdminLayout>
  );
}

// ── Overview Tab (inline — uses page state) ────────────────────────────────────

interface OverviewProps {
  rows: TenantStorageRow[];
  totalUsedMb: number;
  totalQuotaMb: number;
  platformPct: number;
  criticalTenants: TenantStorageRow[];
  warningTenants: TenantStorageRow[];
  topTenants: TenantStorageRow[];
  fileCount: number;
  lastUpdated: Date | null;
  onGoToUsage: () => void;
  onGoToFiles: () => void;
}

function OverviewTab({
  rows, totalUsedMb, totalQuotaMb, platformPct, criticalTenants,
  warningTenants, topTenants, fileCount, lastUpdated, onGoToUsage, onGoToFiles,
}: OverviewProps) {
  const availableMb  = Math.max(0, totalQuotaMb - totalUsedMb);
  const estimatedPct = Math.min(100, platformPct + 8); // conservative 30-day projection (+8% growth estimate)
  const planBreakdown = (['free', 'pro', 'enterprise'] as const).map((plan) => {
    const planRows = rows.filter((r) => r.plan_code === plan);
    const usedMb   = planRows.reduce((s, r) => s + r.usedBytes / (1024 * 1024), 0);
    const quotaMb  = planRows.reduce((s, r) => s + r.storage_quota_mb, 0);
    return { plan, count: planRows.length, usedMb, quotaMb };
  }).filter((b) => b.count > 0);

  return (
    <div className="space-y-6">

      {/* ── Alerts ── */}
      {criticalTenants.length > 0 && (
        <div className="flex items-start gap-3 px-4 py-3.5 bg-red-50 border border-red-200 rounded-xl">
          <span className="w-8 h-8 flex items-center justify-center bg-red-100 rounded-lg shrink-0">
            <i className="ri-alarm-warning-fill text-red-600 text-sm"></i>
          </span>
          <div className="flex-1">
            <p className="text-red-700 text-sm font-semibold">
              {criticalTenants.length} tenant{criticalTenants.length > 1 ? 's' : ''} at critical storage (&ge;90%)
            </p>
            <p className="text-red-500 text-xs mt-0.5">
              {criticalTenants.map((t) => t.name).join(', ')} — increase quota or clean up files immediately.
            </p>
          </div>
          <button
            onClick={onGoToUsage}
            className="flex items-center gap-1.5 px-3 py-1.5 bg-red-600 hover:bg-red-700 text-white text-xs font-semibold rounded-lg cursor-pointer whitespace-nowrap transition-colors"
          >
            Manage
            <i className="ri-arrow-right-line"></i>
          </button>
        </div>
      )}
      {warningTenants.length > 0 && criticalTenants.length === 0 && (
        <div className="flex items-center gap-3 px-4 py-3 bg-amber-50 border border-amber-200 rounded-xl">
          <span className="w-7 h-7 flex items-center justify-center bg-amber-100 rounded-lg shrink-0">
            <i className="ri-error-warning-line text-amber-600 text-sm"></i>
          </span>
          <p className="text-amber-700 text-sm flex-1">
            <strong>{warningTenants.length}</strong> tenant{warningTenants.length > 1 ? 's' : ''} using &ge;70% of quota —
            <span className="text-amber-500 ml-1">{warningTenants.map((t) => t.name).join(', ')}</span>
          </p>
        </div>
      )}

      {/* ── Stats grid ── */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {[
          {
            label:   'Total Quota',
            value:   formatStorageMb(totalQuotaMb),
            sub:     `across ${rows.length} tenants`,
            icon:    'ri-hard-drive-2-line',
            iconCls: 'bg-slate-100 text-slate-600',
          },
          {
            label:   'Total Used',
            value:   formatStorageMb(totalUsedMb),
            sub:     `${platformPct}% of allocated quota`,
            icon:    'ri-database-2-line',
            iconCls: platformPct >= 90 ? 'bg-red-50 text-red-600' : platformPct >= 70 ? 'bg-amber-50 text-amber-600' : 'bg-emerald-50 text-emerald-600',
          },
          {
            label:   'Available',
            value:   formatStorageMb(availableMb),
            sub:     `${100 - platformPct}% free`,
            icon:    'ri-inbox-unarchive-line',
            iconCls: 'bg-emerald-50 text-emerald-600',
          },
          {
            label:   'File Records',
            value:   fileCount.toLocaleString(),
            sub:     'in uploaded_files',
            icon:    'ri-file-list-3-line',
            iconCls: 'bg-slate-100 text-slate-600',
          },
        ].map((s) => (
          <div key={s.label} className="bg-white border border-slate-200 rounded-xl p-5">
            <div className="flex items-start justify-between mb-3">
              <span className={`w-9 h-9 flex items-center justify-center rounded-xl ${s.iconCls} shrink-0`}>
                <i className={`${s.icon} text-base`}></i>
              </span>
            </div>
            <p className="text-[#0f172a] text-2xl font-bold leading-none mb-1">{s.value}</p>
            <p className="text-slate-400 text-xs">{s.label}</p>
            <p className="text-slate-300 text-[11px] mt-0.5">{s.sub}</p>
          </div>
        ))}
      </div>

      {/* ── Platform usage bar + estimated ── */}
      <div className="bg-white border border-slate-200 rounded-xl p-6 space-y-5">
        <div className="flex items-center justify-between">
          <p className="text-[#0f172a] text-sm font-semibold">Platform Usage</p>
          {lastUpdated && (
            <p className="text-slate-300 text-[11px]">
              Updated {lastUpdated.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })}
            </p>
          )}
        </div>

        {/* Current usage */}
        <div>
          <div className="flex items-center justify-between text-xs mb-2">
            <span className="text-slate-500 font-medium">Current Usage</span>
            <span className={`font-bold ${platformPct >= 90 ? 'text-red-600' : platformPct >= 70 ? 'text-amber-600' : 'text-emerald-600'}`}>
              {platformPct}%
            </span>
          </div>
          <div className="w-full bg-slate-100 rounded-full h-3 overflow-hidden">
            <div
              className={`h-3 rounded-full transition-all ${barCls(platformPct)}`}
              style={{ width: `${platformPct}%` }}
            />
          </div>
          <div className="flex justify-between text-[11px] text-slate-400 mt-1.5">
            <span>{formatStorageMb(totalUsedMb)} used</span>
            <span>{formatStorageMb(availableMb)} free</span>
            <span>{formatStorageMb(totalQuotaMb)} total quota</span>
          </div>
        </div>

        {/* Estimated 30-day */}
        <div>
          <div className="flex items-center justify-between text-xs mb-2">
            <span className="text-slate-400 font-medium flex items-center gap-1.5">
              <i className="ri-calendar-line text-slate-300"></i>
              Estimated in 30 days
              <span className="text-slate-300 text-[10px]">(+8% growth est.)</span>
            </span>
            <span className={`font-bold text-xs ${estimatedPct >= 90 ? 'text-red-500' : estimatedPct >= 70 ? 'text-amber-500' : 'text-slate-400'}`}>
              ~{estimatedPct}%
            </span>
          </div>
          <div className="w-full bg-slate-100 rounded-full h-2 overflow-hidden">
            <div
              className={`h-2 rounded-full transition-all opacity-50 ${barCls(estimatedPct)}`}
              style={{ width: `${estimatedPct}%` }}
            />
          </div>
        </div>

        {/* Breakdown by plan */}
        {planBreakdown.length > 0 && (
          <div>
            <p className="text-xs text-slate-400 font-medium mb-3 uppercase tracking-wider">Breakdown by Plan</p>
            <div className="space-y-2">
              {planBreakdown.map((b) => {
                const p = pct(b.usedMb, b.quotaMb);
                return (
                  <div key={b.plan} className="flex items-center gap-3">
                    <span className={`text-[11px] font-bold uppercase w-20 shrink-0 ${
                      b.plan === 'enterprise' ? 'text-emerald-600' :
                      b.plan === 'pro'        ? 'text-amber-600' : 'text-slate-400'
                    }`}>{b.plan} ({b.count})</span>
                    <div className="flex-1 bg-slate-100 rounded-full h-1.5 overflow-hidden">
                      <div
                        className={`h-1.5 rounded-full ${barCls(p)}`}
                        style={{ width: `${p}%` }}
                      />
                    </div>
                    <span className="text-slate-500 text-[11px] w-20 text-right shrink-0">
                      {formatStorageMb(b.usedMb)} / {formatStorageMb(b.quotaMb)}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>

      {/* ── Top tenants + Warnings side by side ── */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">

        {/* Top tenants by usage */}
        <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
          <div className="px-5 py-4 border-b border-slate-100 flex items-center justify-between">
            <p className="text-[#0f172a] text-sm font-semibold">Top 5 by Usage</p>
            <button
              onClick={onGoToUsage}
              className="text-xs text-slate-400 hover:text-[#0f172a] cursor-pointer flex items-center gap-1 transition-colors"
            >
              View all <i className="ri-arrow-right-line text-xs"></i>
            </button>
          </div>
          <div className="divide-y divide-slate-100">
            {topTenants.length === 0 ? (
              <div className="px-5 py-8 text-center">
                <i className="ri-hard-drive-line text-2xl text-slate-200 block mb-2"></i>
                <p className="text-slate-400 text-xs">No storage data yet</p>
              </div>
            ) : (
              topTenants.map((t, i) => {
                const used  = t.usedBytes / (1024 * 1024);
                const p     = pct(used, t.storage_quota_mb);
                return (
                  <div key={t.id} className="px-5 py-3.5 flex items-center gap-3">
                    <span className="text-slate-300 text-xs font-bold w-4 shrink-0">#{i + 1}</span>
                    <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-slate-100 text-slate-600 text-xs font-bold shrink-0">
                      {t.name.charAt(0).toUpperCase()}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between mb-1">
                        <p className="text-slate-800 text-sm font-medium truncate">{t.name}</p>
                        <span className={`text-xs font-bold ml-2 shrink-0 ${p >= 90 ? 'text-red-600' : p >= 70 ? 'text-amber-600' : 'text-slate-400'}`}>
                          {p}%
                        </span>
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="flex-1 bg-slate-100 rounded-full h-1.5 overflow-hidden">
                          <div className={`h-1.5 rounded-full ${barCls(p)}`} style={{ width: `${p}%` }} />
                        </div>
                        <span className="text-slate-400 text-[11px] shrink-0 whitespace-nowrap">
                          {formatStorageMb(used)} / {formatStorageMb(t.storage_quota_mb)}
                        </span>
                      </div>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>

        {/* Storage warnings & status */}
        <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
          <div className="px-5 py-4 border-b border-slate-100">
            <p className="text-[#0f172a] text-sm font-semibold">Status & Warnings</p>
          </div>
          <div className="p-5 space-y-3">

            {/* Provider status */}
            <div className="flex items-center justify-between py-3 px-4 bg-slate-50 rounded-xl border border-slate-200">
              <div className="flex items-center gap-3">
                <span className="w-8 h-8 flex items-center justify-center bg-[#0f172a] rounded-lg">
                  <i className="ri-server-fill text-white text-sm"></i>
                </span>
                <div>
                  <p className="text-slate-700 text-sm font-semibold">VPS Storage</p>
                  <p className="text-slate-400 text-xs">Primary binary store</p>
                </div>
              </div>
              <span className="flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-emerald-50 border border-emerald-200 text-emerald-700 text-[11px] font-semibold">
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse"></span>
                Active
              </span>
            </div>

            <div className="flex items-center justify-between py-3 px-4 bg-slate-50 rounded-xl border border-slate-200">
              <div className="flex items-center gap-3">
                <span className="w-8 h-8 flex items-center justify-center bg-emerald-500 rounded-lg">
                  <i className="ri-database-2-line text-white text-sm"></i>
                </span>
                <div>
                  <p className="text-slate-700 text-sm font-semibold">Supabase (Metadata)</p>
                  <p className="text-slate-400 text-xs">URLs, sizes, types only</p>
                </div>
              </div>
              <span className="flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-emerald-50 border border-emerald-200 text-emerald-700 text-[11px] font-semibold">
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse"></span>
                Active
              </span>
            </div>

            {/* Critical & warning counts */}
            <div className="grid grid-cols-2 gap-3 pt-1">
              <div className={`flex flex-col gap-1 px-4 py-3 rounded-xl border ${
                criticalTenants.length > 0 ? 'bg-red-50 border-red-200' : 'bg-slate-50 border-slate-200'
              }`}>
                <div className="flex items-center gap-2">
                  <i className={`ri-error-warning-fill text-sm ${criticalTenants.length > 0 ? 'text-red-500' : 'text-slate-300'}`}></i>
                  <span className={`text-xl font-bold ${criticalTenants.length > 0 ? 'text-red-600' : 'text-slate-300'}`}>
                    {criticalTenants.length}
                  </span>
                </div>
                <p className="text-slate-500 text-xs">Critical (&ge;90%)</p>
                {criticalTenants.length > 0 && (
                  <p className="text-red-400 text-[11px] leading-snug">
                    {criticalTenants.slice(0, 2).map((t) => t.name).join(', ')}
                    {criticalTenants.length > 2 ? ` +${criticalTenants.length - 2} more` : ''}
                  </p>
                )}
              </div>
              <div className={`flex flex-col gap-1 px-4 py-3 rounded-xl border ${
                warningTenants.length > 0 ? 'bg-amber-50 border-amber-200' : 'bg-slate-50 border-slate-200'
              }`}>
                <div className="flex items-center gap-2">
                  <i className={`ri-alarm-warning-line text-sm ${warningTenants.length > 0 ? 'text-amber-500' : 'text-slate-300'}`}></i>
                  <span className={`text-xl font-bold ${warningTenants.length > 0 ? 'text-amber-600' : 'text-slate-300'}`}>
                    {warningTenants.length}
                  </span>
                </div>
                <p className="text-slate-500 text-xs">Warning (70–89%)</p>
                {warningTenants.length > 0 && (
                  <p className="text-amber-400 text-[11px] leading-snug">
                    {warningTenants.slice(0, 2).map((t) => t.name).join(', ')}
                    {warningTenants.length > 2 ? ` +${warningTenants.length - 2} more` : ''}
                  </p>
                )}
              </div>
            </div>

            {/* Metadata note */}
            <div className="flex items-start gap-2 px-3 py-2.5 bg-slate-50 border border-dashed border-slate-200 rounded-xl">
              <i className="ri-information-line text-slate-300 text-sm mt-0.5 shrink-0"></i>
              <p className="text-slate-400 text-[11px] leading-relaxed">
                <strong className="text-slate-500">storage_used_mb</strong> in <span className="font-mono">saas_tenants</span> is the source of truth,
                updated by the VPS backend on each upload. <span className="font-mono">uploaded_files</span> holds metadata rows only.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* ── File count reconciliation ── */}
      <div className="bg-white border border-slate-200 rounded-xl p-5">
        <div className="flex items-center justify-between mb-4">
          <p className="text-[#0f172a] text-sm font-semibold">File Metadata Records</p>
          <button
            onClick={onGoToFiles}
            className="flex items-center gap-1.5 text-xs text-slate-400 hover:text-[#0f172a] cursor-pointer transition-colors"
          >
            Browse files <i className="ri-arrow-right-line"></i>
          </button>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div className="flex items-center gap-3 p-4 bg-slate-50 rounded-xl border border-slate-200">
            <span className="w-9 h-9 flex items-center justify-center bg-white rounded-lg border border-slate-200 shrink-0">
              <i className="ri-file-list-3-line text-slate-500 text-sm"></i>
            </span>
            <div>
              <p className="text-[#0f172a] text-xl font-bold">{fileCount.toLocaleString()}</p>
              <p className="text-slate-400 text-xs">Total file records</p>
            </div>
          </div>
          <div className="flex items-center gap-3 p-4 bg-slate-50 rounded-xl border border-slate-200">
            <span className="w-9 h-9 flex items-center justify-center bg-white rounded-lg border border-slate-200 shrink-0">
              <i className="ri-database-2-line text-slate-500 text-sm"></i>
            </span>
            <div>
              <p className="text-[#0f172a] text-xl font-bold">{rows.length}</p>
              <p className="text-slate-400 text-xs">Active tenants</p>
            </div>
          </div>
          <div className="flex items-center gap-3 p-4 bg-slate-50 rounded-xl border border-slate-200">
            <span className="w-9 h-9 flex items-center justify-center bg-white rounded-lg border border-slate-200 shrink-0">
              <i className="ri-hard-drive-2-line text-slate-500 text-sm"></i>
            </span>
            <div>
              <p className="text-[#0f172a] text-xl font-bold">{formatStorageBytes(totalUsedMb * 1024 * 1024)}</p>
              <p className="text-slate-400 text-xs">Reported used (VPS)</p>
            </div>
          </div>
        </div>
        <div className="mt-4 flex items-start gap-2 px-3 py-2.5 bg-amber-50 border border-amber-200 rounded-xl">
          <i className="ri-error-warning-line text-amber-500 text-sm mt-0.5 shrink-0"></i>
          <p className="text-amber-700 text-xs leading-relaxed">
            <strong>Important:</strong> File count in <span className="font-mono">uploaded_files</span> may differ from VPS actual files —
            <span className="font-mono"> storage_used_mb</span> is authoritative. The VPS backend updates this field on each upload/delete.
            Supabase only stores metadata rows; binaries live exclusively on VPS.
          </p>
        </div>
      </div>

    </div>
  );
}